import type { AppContextType, OrgMembership } from "@pex/types";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "../types";

function bridgeSchema(client: SupabaseClient<Database>) {
  // biome-ignore lint/suspicious/noExplicitAny: schema() is not typed in the generated Database type
  return (client as unknown as any).schema("bridge");
}

function pulseSchema(client: SupabaseClient<Database>) {
  // biome-ignore lint/suspicious/noExplicitAny: schema() is not typed in the generated Database type
  return (client as unknown as any).schema("pulse");
}

// ---------------------------------------------------------------------------
// hasAppAccess
// ---------------------------------------------------------------------------
// Determines whether a user has access to a given app by checking for a
// context-specific profile record. Each app schema has its own profile table.
//
// Current implementation: bridge only (bridge.facilitators).
// Add other contexts here as each app schema migrates.
// ---------------------------------------------------------------------------
export async function hasAppAccess(
  client: SupabaseClient<Database>,
  userId: string,
  appContext: AppContextType
): Promise<boolean> {
  if (appContext === "bridge") {
    const { data, error } = await bridgeSchema(client)
      .from("facilitators")
      .select("id")
      .eq("account_id", userId)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to check Bridge app access: ${error.message}`);
    }

    return data !== null;
  }

  if (appContext === "pulse") {
    const { data, error } = await pulseSchema(client)
      .from("staff")
      .select("id")
      .eq("account_id", userId)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to check Pulse app access: ${error.message}`);
    }

    return data !== null;
  }

  // circle, habitat schemas not yet migrated — return false until each lands.
  return false;
}

// ---------------------------------------------------------------------------
// getBridgeMiddlewareState
// ---------------------------------------------------------------------------
// Delegates to the get_bridge_middleware_state Postgres RPC, which resolves
// all middleware routing state in a single server-side round-trip.
// See: supabase/migrations/20260511113401_bridge_middleware_state_rpc.sql
// ---------------------------------------------------------------------------
// Status of the currently-selected org (bridge_active_org cookie):
//   "complete"      — active membership AND setup_complete = true
//   "incomplete"    — active membership BUT setup_complete = false
//   "no-membership" — no active membership to this org (stale cookie)
export type OrgSetupStatus = "complete" | "incomplete" | "no-membership";

export interface BridgeMiddlewareState {
  // null when no activeOrgId was supplied
  activeOrgStatus: OrgSetupStatus | null;
  firstCompleteOrgId: string | null;
  hasActiveMembership: boolean;
  hasFacilitator: boolean;
}

export async function getBridgeMiddlewareState(
  client: SupabaseClient<Database>,
  userId: string,
  activeOrgId: string | undefined
): Promise<BridgeMiddlewareState> {
  const { data, error } = await client.rpc("get_bridge_middleware_state", {
    p_user_id: userId,
    p_active_org_id: activeOrgId,
  });

  if (error) {
    throw new Error(`Bridge middleware state RPC failed: ${error.message}`);
  }

  const row = data?.[0];

  if (!row) {
    return {
      hasFacilitator: false,
      hasActiveMembership: false,
      activeOrgStatus: null,
      firstCompleteOrgId: null,
    };
  }

  return {
    hasFacilitator: row.has_facilitator,
    hasActiveMembership: row.has_active_membership,
    // generated type says string but the column is nullable at the SQL level
    activeOrgStatus: (row.active_org_status ?? null) as OrgSetupStatus | null,
    firstCompleteOrgId: row.first_complete_org_id ?? null,
  };
}

// No setup_complete check — the inviting org's setup is the account manager's responsibility.
export async function getFirstInvitedOrgId(
  client: SupabaseClient<Database>,
  userId: string
): Promise<string | null> {
  const { data: facilitator, error: facilitatorError } = await bridgeSchema(
    client
  )
    .from("facilitators")
    .select("id")
    .eq("account_id", userId)
    .maybeSingle();

  if (facilitatorError) {
    throw new Error(
      `Failed to look up facilitator for invited org check: ${facilitatorError.message}`
    );
  }

  if (!facilitator) {
    return null;
  }

  const { data: membership, error: membershipError } = await bridgeSchema(
    client
  )
    .from("organization_facilitators")
    .select("organization_id")
    .eq("facilitator_id", facilitator.id)
    .eq("status", "invited")
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();

  if (membershipError) {
    throw new Error(
      `Failed to look up invited org membership: ${membershipError.message}`
    );
  }

  return (
    (membership as { organization_id: string } | null)?.organization_id ?? null
  );
}

// ---------------------------------------------------------------------------
// getUserApps
// ---------------------------------------------------------------------------
// Returns the app contexts the user has access to. Used by the app switcher.
// Extend as each app schema migrates.
// ---------------------------------------------------------------------------
export async function getUserApps(
  client: SupabaseClient<Database>,
  userId: string
): Promise<AppContextType[]> {
  const apps: AppContextType[] = [];

  const { data: facilitator, error: bridgeError } = await bridgeSchema(client)
    .from("facilitators")
    .select("id")
    .eq("account_id", userId)
    .maybeSingle();

  if (bridgeError) {
    throw new Error(`Failed to check Bridge access: ${bridgeError.message}`);
  }

  if (facilitator) {
    // Only surface Bridge in the app switcher when the user still has an
    // active or invited org membership. Deactivated facilitators retain the
    // facilitators row but should not see Bridge as a destination (#133).
    const { data: activeMembership } = await bridgeSchema(client)
      .from("organization_facilitators")
      .select("id")
      .eq("facilitator_id", facilitator.id)
      .in("status", ["active", "invited"])
      .limit(1)
      .maybeSingle();

    if (activeMembership) {
      apps.push("bridge");
    }
  }

  const { data: staff, error: pulseError } = await pulseSchema(client)
    .from("staff")
    .select("id")
    .eq("account_id", userId)
    .maybeSingle();

  if (pulseError) {
    throw new Error(`Failed to check Pulse access: ${pulseError.message}`);
  }

  if (staff) {
    apps.push("pulse");
  }

  // Other contexts (circle, habitat) added here as schemas land.
  return apps;
}

// ---------------------------------------------------------------------------
// getUserMemberships
// ---------------------------------------------------------------------------
// NOTE: OrgMembership (user_id, role, app_context) does not match the new
// schema. Superseded by getUserOrganizations() + hasAppAccess(). Returns
// empty array for call-site compatibility.
// ---------------------------------------------------------------------------
export function getUserMemberships(
  _client: SupabaseClient<Database>,
  _userId: string,
  _appContext?: AppContextType
): Promise<OrgMembership[]> {
  return Promise.resolve([]);
}

// ---------------------------------------------------------------------------
// createMembership / updateMembership / deleteMembership
// ---------------------------------------------------------------------------
// Not implemented. Memberships are managed directly via
// bridge.organization_facilitators — no generic helpers.
// ---------------------------------------------------------------------------
export function createMembership(
  _client: SupabaseClient<Database>,
  _membership: Omit<OrgMembership, "id" | "created_at">
): Promise<OrgMembership> {
  return Promise.reject(new Error("createMembership: not implemented"));
}

export function updateMembership(
  _client: SupabaseClient<Database>,
  _membershipId: string,
  _updates: Partial<
    Omit<OrgMembership, "id" | "user_id" | "organization_id" | "created_at">
  >
): Promise<OrgMembership> {
  return Promise.reject(new Error("updateMembership: not implemented"));
}

export function deleteMembership(
  _client: SupabaseClient<Database>,
  _membershipId: string
): Promise<void> {
  return Promise.reject(new Error("deleteMembership: not implemented"));
}
